import { Ellipsis, MapPin, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router';
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
  DropdownTrigger,
} from '@/components/atoms/dropdown';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/tooltip';
import type { IProject } from '@/types/api/IProject';

interface ProjectsListActionProps {
  project: IProject;
  onDelete: (project: IProject) => void;
}

export function ProjectsListAction(props: ProjectsListActionProps) {
  const { project, onDelete } = props;
  const navigate = useNavigate();

  const hasWorkspace = !!project.workspace_id;
  // workspace_name comes from a LEFT JOIN and is normally present whenever
  // workspace_id is, but an optimistic update can briefly set the id alone.
  // Gate the tooltip on the name so we never pop an empty balloon.
  const hasWorkspaceName = hasWorkspace && !!project.workspace_name;

  function handleOpenInMap() {
    if (!project.workspace_id) return;
    navigate(`/map?workspace=${project.workspace_id}`);
  }

  // The dropdown item is icon + short label; carry the workspace name (or the
  // reason it is disabled) in the accessible name so keyboard/SR users get the
  // same context the hover tooltip gives sighted users (the tooltip alone is
  // not reachable from inside a menu composite).
  function buildOpenInMapLabel(): string {
    if (hasWorkspaceName) return `Open in Map — ${project.workspace_name}`;
    if (hasWorkspace) return 'Open in Map';
    return 'Open in Map (no workspace assigned)';
  }

  const openInMapItem = (
    <DropdownItem
      className="flex items-center font-normal text-base gap-2"
      disabled={!hasWorkspace}
      onClick={handleOpenInMap}
      aria-label={buildOpenInMapLabel()}
    >
      <MapPin size={18} aria-hidden="true" /> Open in Map
    </DropdownItem>
  );

  return (
    <Dropdown>
      <DropdownTrigger asChild>
        <button className="flex cursor-pointer" type="button" aria-label="Project actions">
          <Ellipsis size={18} className="text-muted-foreground" aria-hidden="true" />
        </button>
      </DropdownTrigger>
      <DropdownContent>
        {hasWorkspaceName ? (
          <Tooltip>
            <TooltipTrigger asChild>{openInMapItem}</TooltipTrigger>
            <TooltipContent>{project.workspace_name}</TooltipContent>
          </Tooltip>
        ) : (
          openInMapItem
        )}
        <DropdownSeparator />
        <DropdownItem
          className="flex items-center text-destructive hover:text-destructive font-normal text-base gap-2"
          onClick={() => onDelete(project)}
        >
          <Trash2 size={18} aria-hidden="true" /> Delete
        </DropdownItem>
      </DropdownContent>
    </Dropdown>
  );
}
